import {CreateAccessor} from "mobx-graphlink";
import {MaterialLayer, MaterialRecipe, ParamSchema} from "../../../Terrain/Materials/@MaterialTypes.js";
import {RESOLVER_DEPTH_CAP, RecipeResolutionError, ResolvedRecipe, resolveRecipeFromChain} from "../../../Terrain/Materials/RecipeResolverCore.js";
import {GetTerrainMaterialsMods} from "./$mapObject.js";
import {builtInRecipes} from "../../../Terrain/Materials/MaterialFuncRegistry.js";

/** Gets all material recipes usable in the given map (ie. all built-ins + the union of `userRecipes` on
 *  every enabled `Mod_TerrainMaterials` mod).  TerrainRenderer loads this exact set, so the
 *  region-assignment UI must offer the same ids.  Duplicate ids resolve to the same entry. */
export const GetProcMatRecipes = CreateAccessor((mapID: string): MaterialRecipe[]=>{
	//const byID = new Map<string, MaterialRecipe>();
	const result = [
		...builtInRecipes.values(),
	];
	for (const mod of GetTerrainMaterialsMods(mapID)) {
		for (const r of mod.userRecipes) result.push(r);
	}
	return result;
});
export const GetProcMatRecipesByID = CreateAccessor((mapID: string): Map<string, MaterialRecipe>=>{
	const result = GetProcMatRecipes(mapID);
	return result.ToMap(a=>a.id, a=>a);
});

export const GetProcMatRecipe = CreateAccessor((recipeID: string|n, mapID: string): MaterialRecipe | undefined=>{
	return GetProcMatRecipesByID(mapID).get(recipeID!);
});

/** Resolve the lineage of a recipe (root → leaf) for the lineage display + cost-model computation.
 *  Returns the chain + the resolved recipe; warnings are surfaced (base drift). */
export function GetProcMatRecipeLineage(id: string, mapID: string): {chain: string[]; resolved: ResolvedRecipe | null; error: string | null} {
	const ids: string[] = [];
	let cur = id;
	try {
		for (let i = 0; i < RESOLVER_DEPTH_CAP + 1; i++) {
			ids.push(cur);
			const r = GetProcMatRecipe(cur, mapID);
			if (r == null) {
				return {chain: ids, resolved: null, error: `recipe "${cur}" not found`};
			}
			if (r.baseRecipeID == null) break;
			cur = r.baseRecipeID;
		}
		const resolved = ResolveRecipe(id, mapID);
		return {chain: ids, resolved, error: null};
	} catch (err) {
		return {chain: ids, resolved: null, error: (err as Error).message};
	}
}

/** Resolve a recipe id to a flat ResolvedRecipe. Throws RecipeResolutionError on unknown id,
 *  cycle, or depth-cap overflow; emits warnings (not errors) for overrides/disables naming ids
 *  absent from the resolved base (the expected symptom of base drift). */
export const ResolveRecipe = CreateAccessor((id: string, mapID: string): ResolvedRecipe=>{
	// Walk the base chain root-ward, tracking cycles + depth.
	const chain: MaterialRecipe[] = [];
	const visited = new Set<string>();
	let cur = GetProcMatRecipe(id, mapID);
	if (cur == null) throw new RecipeResolutionError(`recipe "${id}" not found in catalogue`);
	while (cur != null) {
		if (visited.has(cur.id)) throw new RecipeResolutionError(`cycle detected: ${[...visited, cur.id].join(" -> ")}`);
		visited.add(cur.id);
		if (visited.size > RESOLVER_DEPTH_CAP) throw new RecipeResolutionError(`depth cap (${RESOLVER_DEPTH_CAP}) exceeded: ${[...visited].join(" -> ")}`);
		chain.unshift(cur);
		if (cur.baseRecipeID == null) break;
		cur = GetProcMatRecipe(cur.baseRecipeID, mapID);
	}

	return resolveRecipeFromChain(chain);
});

/** Resolve a SET of recipe ids (order preserved). Each id resolves through the per-id memo, so a
 *  set resolve only pays for uncached members. Phase 6's warm-up enumerates sets and dedupes by
 *  {@link recipeSetHash} — params-only variants collapse to one hash (D1/D10). */
export function ResolveRecipeSet(ids: string[], mapID: string): ResolvedRecipe[] {
	return ids.map(id=>ResolveRecipe(id, mapID));
}